import { DatasetVersionCard } from "@/components/DatasetVersionCard";
import EntityProfilePic from "@/components/EntityProfilePic";
import ProfilePic from "@/components/ProfilePic";
import { Divided, DividedItem } from "@/components/Divided";
import { File04Icon } from "@/icons";
import { AsideContent } from "@/kit/ui/aside";
import { Badge } from "@/kit/ui/badge";
import { ReactNode, useTransition } from "react";
import { FormattedMessage } from "react-intl";
import { graphql, useFragment, useRefetchableFragment } from "react-relay";
import { generatePath, Link } from "react-router";
import { DatasetVersionAsideContentAuthorsFragment$key } from "./__generated__/DatasetVersionAsideContentAuthorsFragment.graphql";
import { DatasetVersionAsideContentFragment$key } from "./__generated__/DatasetVersionAsideContentFragment.graphql";
import DatasetDOI from "./DatasetDOI";
import { assertNever, invariant } from "@/utils/helpers";
import ExpandableList from "./ExpandableList";
import TagList from "./TagList";
import { DatasetVersionEditSheet } from "./DatasetVersionEdit";

const DatasetVersionAsideContentFragment = graphql`
  fragment DatasetVersionAsideContentFragment on DatasetVersion {
    id
    version
    license
    publishedAt
    viewerCanUpdate: viewerCan(action: UPDATE_DATASET_VERSION)
    doi {
      full
    }
    dataset {
      id
      slug
      name
      shortDescription
      private
      viewerCanUpdate: viewerCan(action: UPDATE_DATASET)
      ...TagListFragment
      ...DatasetVersionCreateButtonFragment
    }
    ...DatasetVersionCardFragment
    ...DatasetVersionEditSheet
    ...DatasetVersionAsideContentAuthorsFragment
    ...DatasetDOIFragment
    authorsCount: authors(first: 0) {
      totalCount
    }
  }
`;

const DatasetVersionAsideContentAuthorsFragment = graphql`
  fragment DatasetVersionAsideContentAuthorsFragment on DatasetVersion
  @refetchable(
    queryName: "DatasetVersionAsideContentAuthorsFragmentPaginationQuery"
  )
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int", defaultValue: 5 }
  ) {
    authorsPreview: authors(first: $count, after: $cursor)
      @connection(
        key: "DatasetVersionAsideContentAuthorsFragment_authorsPreview"
      ) {
      edges {
        cursor
        node {
          __typename
          ... on User {
            id
            username
            displayName
            ...EntityProfilePicFragment @arguments(thumbnail: true)
          }
          ... on UnregisteredUser {
            fullName
          }
        }
      }
    }
  }
`;

interface Props {
  datasetVersion: DatasetVersionAsideContentFragment$key;
  analytics: ReactNode;
  editSheetOpen: boolean;
  onEditSheetOpenChange: (open: boolean) => void;
}

export function DatasetVersionAsideContent(props: Props) {
  const { dataset, ...datasetVersion } = useFragment(
    DatasetVersionAsideContentFragment,
    props.datasetVersion,
  );

  return (
    <AsideContent sticky={false}>
      <h3 className="text-xl font-semibold text-grey-900 xl:hidden block">
        <FormattedMessage defaultMessage="Dataset Card" />
      </h3>
      <DatasetVersionCard datasetVersion={datasetVersion} />
      <DatasetVersionEditSheet
        datasetVersion={datasetVersion}
        canEdit={dataset.viewerCanUpdate && datasetVersion.viewerCanUpdate}
        open={props.editSheetOpen}
        onOpenChange={props.onEditSheetOpenChange}
      />
      <Divided>
        <DividedItem
          title={<FormattedMessage defaultMessage="About" />}
          description={
            !dataset.shortDescription && (
              <FormattedMessage defaultMessage="Add short description to let others know what this dataset is about." />
            )
          }
        >
          {dataset.shortDescription && (
            <p className="text-grey-800 font-normal text-sm leading-tight">
              {dataset.shortDescription}
            </p>
          )}
        </DividedItem>

        {!dataset.private && datasetVersion.publishedAt && (
          <DividedItem
            title={
              <FormattedMessage defaultMessage="Cite this dataset (BibTeX)" />
            }
          >
            {datasetVersion.doi ? (
              <DatasetDOI datasetVersion={datasetVersion} />
            ) : (
              <DatasetDOI.Pending datasetVersion={datasetVersion} />
            )}
          </DividedItem>
        )}

        <DividedItem
          title={<FormattedMessage defaultMessage="Categories" />}
          description={
            <FormattedMessage defaultMessage="Add categories to make your dataset easier to find." />
          }
        >
          <TagList taggable={dataset} />
        </DividedItem>

        <DividedItem
          title={<FormattedMessage defaultMessage="Licenses" />}
          description={
            <FormattedMessage defaultMessage="Make it clear to the community how they can use your dataset" />
          }
        >
          {datasetVersion.license && (
            <div className="flex flex-row items-center gap-2">
              <File04Icon className="text-grey-600 w-5 h-5" />
              <p className="text-grey-700 font-normal text-sm leading-5 line-clamp-1">
                {datasetVersion.license}
              </p>
            </div>
          )}
        </DividedItem>

        <DividedItem
          title={<FormattedMessage defaultMessage="Authors" />}
          description={
            <FormattedMessage defaultMessage="Add dataset authors." />
          }
        >
          <AuthorList
            datasetVersion={datasetVersion}
            total={datasetVersion.authorsCount.totalCount}
          />
        </DividedItem>

        {props.analytics}
      </Divided>
    </AsideContent>
  );
}

interface AuthorListProps {
  datasetVersion: DatasetVersionAsideContentAuthorsFragment$key;
  total: number;
}

function AuthorList({ datasetVersion, total }: AuthorListProps) {
  const [_, startTransition] = useTransition();
  const [{ authorsPreview }, refetch] = useRefetchableFragment(
    DatasetVersionAsideContentAuthorsFragment,
    datasetVersion,
  );

  return (
    <ExpandableList
      total={total}
      onExpand={() =>
        startTransition(() => {
          refetch({ count: total }, { fetchPolicy: "store-or-network" });
        })
      }
    >
      {authorsPreview.edges.map(({ node, cursor }) => {
        invariant(node.__typename !== "%other");
        switch (node.__typename) {
          case "User":
            return (
              <Link
                key={cursor}
                to={generatePath("/:username", {
                  username: node.username ?? "",
                })}
              >
                <Badge className="rounded-full">
                  <div className="flex flex-row items-center justify-start gap-1.5">
                    <EntityProfilePic entity={node} size="4" />
                    <span>{node.username}</span>
                  </div>
                </Badge>
              </Link>
            );
          case "UnregisteredUser":
            return (
              <Badge key={cursor} className="rounded-full">
                <div className="flex flex-row items-center justify-start gap-1.5">
                  <ProfilePic className="w-4" alt={node.fullName} />
                  <span>{node.fullName}</span>
                </div>
              </Badge>
            );
          default:
            assertNever(node);
        }
      })}
    </ExpandableList>
  );
}
